import { Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { Tc } from 'src/app/components/core/_infrastructure/types/common';
import { FilterSearchingTwAforoViewModel } from '../aforos.types';
import { CultivoViewModel } from '../../../../_campanias/cultivos/cultivo.types';
import { TwRecintoViewModel } from '../../../../_campanias/recintos/recinto.types';
import { TwTcVariedadCamViewModel } from '../../../especies/variedades/variedad.types';
import { debounceTime } from 'rxjs';

@Component({
    selector: 'app-aforo-filter',
    templateUrl: './aforo-filter.component.html',
})
export class AforoFilterComponent extends ComponentBase implements OnInit, OnChanges {
    @Input() comunidadesAutonomas: Tc[];
    @Input() provincias: Tc[];
    @Input() recintos: TwRecintoViewModel[];
    @Input() cultivos: CultivoViewModel[];
    @Input() variedades: TwTcVariedadCamViewModel[];
    @Input() lastFilter: FilterSearchingTwAforoViewModel;

    @Output() comunidadAutonomaSelected = new EventEmitter<number>();
    @Output() provinciaSelected = new EventEmitter<number>();
    @Output() recintoSelected = new EventEmitter<number>();
    @Output() search = new EventEmitter<FilterSearchingTwAforoViewModel>();
    @Output() cleanForm = new EventEmitter<any>(); // Informa que el formulario se ha reseteado

    isCollapsed = false;

    constructor(
        private fb: FormBuilder,
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.createForm();
        this.recoverLastSearch();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.form) {
            return;
        }
        if (changes?.lastFilter?.currentValue) {
            this.patchForm();
        }
        /*
        if (changes?.cultivos?.currentValue) {
            this.cultivos.forEach(cultivo => {
                // Buscar la descripción de la variedad correspondiente al IdVariedad
                const variedad = this.variedades.find(v => v.Id === cultivo.IdVariedad);
                if (variedad) {
                    cultivo.Variedad = variedad.Descripcion; // Agregar la descripción de la variedad al cultivo
                }
            });
        }
            */
    }

    private createForm(): void {
        this.form = this.fb.group({
            IdComunidadAutonoma: null,
            IdProvincia: null,
            IdRecinto: null,
            IdCultivo: null,
            IdVariedad: null,
            Anyo: null,
            Descripcion: null,
        });
    }

    /**
     * Recover last search from the previous payload
     */
    private recoverLastSearch(): void {
        if (this.lastFilter) {
            this.patchForm();

            setTimeout(() => {
                this.onSearch(null);
            }, 200);

            // As soon as the user changes the "filter", the previous lastFilter pagination values has no sense
            /*
            this.form.valueChanges.pipe(debounceTime(500)).subscribe(values => {
                if (values) {
                    this.clearPaginationFilter();
                }
            });
            */
        }
    }

    /**
     * Recover last search from the previous payload
     */
    private patchForm(): void {
        this.form.setValue({
            IdComunidadAutonoma: this.lastFilter.IdComunidadAutonoma,
            IdProvincia: this.lastFilter.IdProvincia,
            IdRecinto: this.lastFilter.IdRecinto,
            IdCultivo: this.lastFilter.IdCultivo,
            IdVariedad: this.lastFilter.IdVariedad,
            Anyo: this.lastFilter.Anyo,
            Descripcion: this.lastFilter.Descripcion,
        });
        this.form.markAsDirty();

        /*
        if (this.lastFilter.IdComunidadAutonoma) {
            this.onSelectComunidadAutonoma(null);
        }

        if (this.lastFilter.IdProvincia) {
            this.onSelectProvincia(null);
        }
        */
    }

    /**
     * Manejador del evento para limpiar el formulario.
     * @param $event Evento javascript
     */
    onResetForm($event: any): void {
        $event.stopPropagation();
        this.form.setValue({
            IdComunidadAutonoma: null,
            IdProvincia: null,
            IdRecinto: null,
            IdCultivo: null,
            IdVariedad: null,
            Anyo: null,
            Descripcion: null,
        });

        this.form.markAsPristine();

        this.searchingButton = false;
        this.isCollapsed = false;

        this.cleanForm.emit();
    }

    /**
     * We don't clear the whole filter but only the fields related to the pagination.
     */
    private clearPaginationFilter() {
        if (!this.lastFilter) {
            return;
        }
        this.lastFilter.PageNumber = 0;
        this.lastFilter.Asc = 0;
        this.lastFilter.OrderBy = '';
    }

    /**
     * Manejador del evento de búsqueda
     * @param $event Evento Javascript
     */
    onSearch($event: any): void {
        $event?.stopPropagation();
        this.searchingButton = true;
        this.isCollapsed = true;

        const formRawValue = this.form.getRawValue();

        const payload: FilterSearchingTwAforoViewModel = {
            ...formRawValue,
            PageNumber: this.lastFilter?.PageNumber || 0,
            Asc: this.lastFilter?.Asc || 0,
            OrderBy: this.lastFilter?.OrderBy || '',
        };

        this.search.emit(payload);
    }

    onSelectComunidadAutonoma($event: any): void {
        const idComunidadAutonoma = this.form.controls.IdComunidadAutonoma.value;
        this.comunidadAutonomaSelected.emit(idComunidadAutonoma);
        this.form.patchValue({
            IdProvincia: null,
            IdRecinto: null,
            IdCultivo: null,
        });
        this.provincias = [];
        this.recintos = [];
        this.cultivos = [];
    }

    onSelectProvincia($event: any): void {
        const idProvincia = this.form.controls.IdProvincia.value;
        this.provinciaSelected.emit(idProvincia);
        this.form.patchValue({
            IdRecinto: null,
            IdCultivo: null,
        });
        this.recintos = [];
        this.cultivos = [];
    }

    onSelectRecinto($event: any): void {
        const idRecinto = this.form.controls.IdRecinto.value;
        this.recintoSelected.emit(idRecinto);
        this.form.patchValue({
            IdCultivo: null,
        });
        this.cultivos = [];
    }

    onInputChange(event: any): void {
        const value = event.target.value;
        event.target.value = value.replace(/[^0-9]/g, '').slice(0, 4);
    }

    /**
     * Para controlar la propiedad disabled del botón de reset.
     * @returns boolean
     */
    formIsUntouched(): boolean {
        return this.form.pristine || !this.form.dirty;
    }

    /**
     * Para controlar la propiedad disabled del botón de búsqueda.
     * Mientras no estén cargados los grupos, no se habilita el botón.
     * @returns boolean
     */
    get disableSearchBtn(): boolean {
        return this.isCollapsed;
    }
}
